import {
  type CanvasElement,
  type CanvasElementOverrides,
  type RenderTarget,
  type SlotData,
} from '@blaze-runtime/engine-domain';
import {
  type ElementPropsSection,
  type PropsUpdate,
  UNSET_PROPERTY_KEY,
} from '@blaze-shared/elements-domain';
import { deleteUndefinedValuesMutably } from '@blaze-shared/shared-util';
import { deleteValueByPath, getValueByPath, setValueByPath } from '@blaze-shared/workflow-domain';

import { hasProps, hasSlots } from '../../../canvas-elements/element-model/element-props-guards';

//#region Element props
export function setElementPropsMutably(
  element: CanvasElement,
  updates: PropsUpdate[],
  target: RenderTarget = 'desktop',
  exclusive = false,
  removeUndefinedFor?: ElementPropsSection[],
): void {
  updates.forEach(({ path, value }) => {
    setPropsValueMutably(element, path, value, target, exclusive, removeUndefinedFor);
  });
}

export function setPropsValueMutably(
  element: CanvasElement,
  path: string,
  value: unknown,
  target: RenderTarget = 'desktop',
  exclusive = false,
  removeUndefinedFor?: ElementPropsSection[],
): void {
  if (path.startsWith('$$')) {
    return;
  }

  if (path.startsWith('root.')) {
    setValueByPath(element, path.replace('root.', ''), value);
    trySetElementSizeDirty(element, path);
  } else if (hasProps(element)) {
    setElementPropsByTargetMutably(element, path, value, target);

    if (target === 'desktop' && exclusive) {
      revertPropsOverrideMutably(element, path, 'tablet');
      revertPropsOverrideMutably(element, path, 'mobile');
    }
    for (const section of removeUndefinedFor ?? []) {
      deleteUndefinedValuesMutably(getValueByPath(element.props, section));
    }
    trySetElementFieldDirty(element, path);
  }
}

function setElementPropsByTargetMutably(
  element: CanvasElement,
  path: string,
  value: unknown,
  target: RenderTarget = 'desktop',
): void {
  if (!hasProps(element)) {
    return;
  }

  if (target === 'desktop') {
    setValueByPath(element.props, path, value);
    return;
  }

  element.overrides ??= {};
  element.overrides[target] ??= { props: {} };
  setValueByPath(element.overrides[target].props, path, value);
}

function trySetElementSizeDirty(element: CanvasElement, path: string): void {
  if (path === 'root.width') {
    return setValueByPath(element, 'props.dirty.width', true);
  } else if (path === 'root.height') {
    return setValueByPath(element, 'props.dirty.height', true);
  }
}

function trySetElementFieldDirty(element: CanvasElement, path: string): void {
  if (path.includes('.dirty.')) {
    return;
  }
  if (path === 'content.title.label') {
    return setValueByPath(element, 'props.dirty.label', true);
  } else if (path === 'content.title.width') {
    return setValueByPath(element, 'props.dirty.labelWidth', true);
  } else if (path === 'content.input.format') {
    return setValueByPath(element, 'props.dirty.format', true);
  } else if (path === 'content.input.precision') {
    return setValueByPath(element, 'props.dirty.precision', true);
  } else if (path === 'content.input.currency') {
    return setValueByPath(element, 'props.dirty.currency', true);
  } else if (path === 'content.input.duration') {
    return setValueByPath(element, 'props.dirty.duration', true);
  } else if (path === 'content.input.dateFormat') {
    return setValueByPath(element, 'props.dirty.dateFormat', true);
  } else if (path === 'content.input.dateTimeFormat') {
    return setValueByPath(element, 'props.dirty.dateTimeFormat', true);
  } else if (
    path.startsWith('content.columns.') ||
    path.startsWith('content.customFields.') ||
    path.startsWith('content.card.customFields.') ||
    path.startsWith('content.markers.customFields.')
  ) {
    const colPath = `props.${path.substring(0, path.lastIndexOf('.'))}.dirty`;
    if (path.endsWith('.label')) {
      return setValueByPath(element, `${colPath}.label`, true);
    } else if (path.endsWith('.format') || path.endsWith('.displayFormat')) {
      return setValueByPath(element, `${colPath}.format`, true);
    } else if (path.endsWith('.precision')) {
      return setValueByPath(element, `${colPath}.precision`, true);
    } else if (path.endsWith('.currency')) {
      return setValueByPath(element, `${colPath}.currency`, true);
    } else if (path.endsWith('.duration')) {
      return setValueByPath(element, `${colPath}.duration`, true);
    } else if (path.endsWith('.dateFormat')) {
      return setValueByPath(element, `${colPath}.dateFormat`, true);
    } else if (path.endsWith('.dateTimeFormat')) {
      return setValueByPath(element, `${colPath}.dateTimeFormat`, true);
    }
  }
}
//#endregion

//#region Slot props
export function setSlotPropsMutably(
  element: CanvasElement,
  slotId: string,
  path: string,
  value: unknown,
  target: RenderTarget = 'desktop',
  exclusive = false,
): void {
  if (!hasSlots(element)) {
    return;
  }

  const slot = element.slots[slotId];
  if (!slot) {
    return;
  }

  slot.props ??= {};

  setSlotPropsByTargetMutably(slot, path, value, target);

  if (target === 'desktop' && exclusive) {
    revertPropsOverrideMutably(slot, path, 'tablet');
    revertPropsOverrideMutably(slot, path, 'mobile');
  }
}

function setSlotPropsByTargetMutably(
  slot: SlotData,
  path: string,
  value: unknown,
  target: RenderTarget = 'desktop',
): void {
  if (target === 'desktop') {
    setValueByPath(slot.props, path, value);
    return;
  }

  slot.overrides ??= {};
  slot.overrides[target] ??= { props: {} };
  setValueByPath(slot.overrides[target].props, path, value);
}
//#endregion

export function revertPropsOverrideMutably(
  elementOrSlot: { overrides?: CanvasElementOverrides },
  propsPath: string,
  target: RenderTarget,
  setOnElement = false,
  setOnStyle = false,
): void {
  if (target === 'desktop') {
    console.error('Deleting props by target is not supported for desktop props');
    return;
  }

  const path = `overrides.${target}.props.${propsPath}`;

  if (setOnStyle && !setOnElement) {
    setValueByPath(elementOrSlot, path, UNSET_PROPERTY_KEY);
    return;
  }

  if (!elementOrSlot.overrides?.[target]?.props) {
    return;
  }

  deleteValueByPath(elementOrSlot, path, true);
}
